/**
 * How a Manifest Review board names a resident, and how it says what a
 * closed case was, without ever showing a raw id.
 *
 *   ResidentName   the name, or -- when eMerge holds none -- a labelled
 *                  marker that says so in words ("Name not recorded"), with
 *                  the CounselEAR id when one is known. eMerge receives a
 *                  resident's name from CounselEAR after the resident itself
 *                  (backend/src/services/counselear/nameFill.js), so a
 *                  nameless resident is a real resident whose name has not
 *                  arrived, not an empty row.
 *   closedWords    "DUPLICATE OF <uuid>" (the closed_reason the duplicate
 *                  closers write) read as words: a duplicate, never the id.
 *   DuplicateOf    the duplicate's kept case, named by its resident and
 *                  building, linked to open that case.
 */
import React, { useEffect, useState } from 'react';
import { advocateApi } from '../../advocate/advocateApi';

export const NAME_NOT_RECORDED = 'Name not recorded';

export function residentName(p: { first_name?: string | null; last_name?: string | null }): string | null {
  const n = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
  return n || null;
}

/** The name, or the label a row says instead (for aria labels and messages). */
export function residentLabel(p: { first_name?: string | null; last_name?: string | null; counselear_id?: string | null }): string {
  return residentName(p) || (p.counselear_id ? `CounselEAR ${p.counselear_id} (name not recorded)` : NAME_NOT_RECORDED);
}

export function ResidentName({ p, why }: { p: { first_name?: string | null; last_name?: string | null; counselear_id?: string | null }; why?: string }) {
  const n = residentName(p);
  if (n) return <strong>{n}</strong>;
  return (
    <span className="mf-noname" title={why || 'eMerge holds no name for this resident yet. CounselEAR sends the name after the resident.'}>
      <span className="mf-noname__mark" aria-hidden="true">?</span>
      <strong className="mf-noname__word">{NAME_NOT_RECORDED}</strong>
      {p.counselear_id ? <span className="board-src">CounselEAR {p.counselear_id}</span> : null}
    </span>
  );
}

const DUP_RE = /^\s*DUPLICATE OF\s+([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\s*$/i;
const UUID_ANYWHERE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** What a closed case closed as, in words: `{ word, duplicateOf }`. A raw id never survives into `word`. */
export function closedWords(raw: string | null | undefined): { word: string | null; duplicateOf: string | null } {
  if (!raw) return { word: null, duplicateOf: null };
  const m = String(raw).match(DUP_RE);
  if (m) return { word: 'duplicate', duplicateOf: m[1].toLowerCase() };
  // Any other closing words that carry an id keep their words and lose the id.
  const cleaned = String(raw).replace(UUID_ANYWHERE, '').replace(/\s{2,}/g, ' ').trim();
  return { word: cleaned || null, duplicateOf: null };
}

export const caseHref = (caseId: string) => `/portal/patient-advocate?view=all&case=${encodeURIComponent(caseId)}`;

/**
 * "Duplicate of Ada Fixture, North Ridge": the kept case, read by id from the
 * case route the PA workspace uses, and linked to open it there. While it
 * loads, or when it cannot be read, it says "another case of this resident"
 * -- never the id.
 */
export function DuplicateOf({ caseId, selfId, selfName, selfFacility }: { caseId: string; selfId?: string; selfName?: string | null; selfFacility?: string | null }) {
  const [who, setWho] = useState<{ name: string; facility: string | null } | null>(
    selfId && selfId === caseId ? { name: selfName || NAME_NOT_RECORDED, facility: selfFacility || null } : null,
  );
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (selfId && selfId === caseId) return undefined;
    let live = true;
    advocateApi.case(caseId)
      .then((r) => {
        if (!live) return;
        if (r.success && r.data) {
          const c = r.data.case;
          setWho({ name: residentName(c) || NAME_NOT_RECORDED, facility: c.facility_name || null });
        } else setFailed(true);
      })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [caseId, selfId]);
  const words = who ? `${who.name}${who.facility ? `, ${who.facility}` : ''}` : failed ? 'another case of this resident' : 'another case of this resident…';
  return (
    <span className="mf-dup">
      Duplicate of <a href={caseHref(caseId)}>{words}</a>
      {selfId === caseId ? <span className="board-src"> (the case shown here)</span> : null}
    </span>
  );
}
